import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.submitContact(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '778.960.2377',
      link: 'tel:+17789602377'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@islandcoastalelectric.ca',
      link: 'mailto:info@islandcoastalelectric.ca'
    },
    {
      icon: MapPin,
      title: 'Location',
      content: '2668 Sooke Road, Victoria, BC V9B 1Y6',
      link: null
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon-Fri: 7AM-6PM, Sat: 8AM-4PM',
      link: null
    }
  ];

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
              Contact Us
            </h1>
            <p className="text-white/70 text-lg">
              Have a question or need a quote? Reach out to our team and we'll get back to you as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-primary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-secondary-dark p-8 rounded-lg border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
                
                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-electric-teal mx-auto mb-4" />
                    <h3 className="text-white font-bold text-xl mb-2">Message Sent!</h3>
                    <p className="text-white/70">We'll get back to you as soon as possible.</p>
                    <Button 
                      onClick={() => setSubmitted(false)}
                      className="mt-6 bg-electric-teal text-primary-dark font-bold"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <Label htmlFor="name" className="text-white mb-2 block">Name</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                        placeholder="Your name"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                        placeholder="your@email.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-white mb-2 block">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-primary-dark border-white/20 text-white focus:border-electric-teal"
                        placeholder="(250) 555-0123"
                        required
                        data-testid="contact-phone"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="message" className="text-white mb-2 block">Message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="bg-primary-dark border-white/20 text-white focus:border-electric-teal min-h-[150px]"
                        placeholder="Tell us about your project..."
                        required
                        data-testid="contact-message"
                      />
                    </div>
                    
                    <Button 
                      type="submit"
                      disabled={loading}
                      className="w-full bg-electric-teal text-primary-dark font-bold uppercase tracking-wider py-4 hover:bg-white transition-all btn-glow"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-electric-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-electric-teal" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{info.title}</h3>
                      {info.link ? (
                        <a 
                          href={info.link}
                          className="text-white/70 hover:text-electric-teal transition-colors"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-white/70">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map Placeholder */}
              <div className="mt-12">
                <h3 className="text-white font-bold text-xl mb-4">Service Area</h3>
                <div className="aspect-[16/9] bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/18816918/pexels-photo-18816918.jpeg"
                    alt="Service Area Map"
                    className="w-full h-full object-cover opacity-60"
                  />
                </div>
                <p className="text-white/60 text-sm mt-4">
                  Serving Victoria, Nanaimo, Duncan, Courtenay, Campbell River, and all communities across Vancouver Island.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
